import React, { FC, ReactNode, forwardRef } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { NgTooltip, SkeletonLoader } from '../../UI';
import { useSlideThumbnailPolling, useIsVisible } from '../../../hooks';
import { viewStateSelectors } from '../../../store';
import { SlideRowDisplayLevel } from '../../../interfaces';
import { SLIDE_ROW_LEVEL_COLOR, SLIDE_ROW_LEVEL_BACKGROUND } from './SlideLevel';

export const SLIDE_ROW_SKELETON_HEIGHT = 194;
export const SLIDE_ROW_SKELETON_COUNT = 6;

interface SlideRowProps {
  slideNumber?: number;
  title?: string;
  thumbnailUrl?: string | null;
  thumbnailBase64?: string;
  viewStateKey?: string;
  rightSlot?: ReactNode;
  level?: SlideRowDisplayLevel;
  isHighlighted?: boolean;
  onClick?: () => void;
}

const StyledRow = styled.div<{ $level?: SlideRowDisplayLevel; $isHighlighted?: boolean; $clickable?: boolean }>`
  width: 100%;
  border: 1px solid
    ${({ theme, $level, $isHighlighted }) =>
      $isHighlighted ? theme.palette.black : $level ? SLIDE_ROW_LEVEL_COLOR[$level] : theme.palette.cardBorder};
  background-color: ${({ $level }) => ($level ? SLIDE_ROW_LEVEL_BACKGROUND[$level] : 'transparent')};
  border-radius: ${({ theme }) => theme.radius.card};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  ${({ theme, $clickable, $isHighlighted }) =>
    $clickable &&
    !$isHighlighted &&
    `&:hover {
      border-color: ${theme.palette.textLinkColor};
    }`}
`;

const StyledThumbnailContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 160px;
`;

const StyledThumbnailImage = styled.img`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const StyledFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.small};
  min-height: 32px;
  padding: 0 ${({ theme }) => theme.spacing.small};
  border-top: 1px solid ${({ theme }) => theme.palette.cardBorder};
`;

const StyledCaption = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  flex: 1;
  min-width: 0;
  overflow: hidden;
  font-size: ${({ theme }) => theme.fontSize.xs};
`;

const StyledSlideNumber = styled.span`
  color: ${({ theme }) => theme.palette.textDimmed};
  flex-shrink: 0;
`;

const StyledSlideTitle = styled.span`
  color: ${({ theme }) => theme.palette.text};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledRightSlot = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const StyledPolledThumbnailWrapper = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const PolledThumbnailImage: FC<{ thumbnailUrl: string; themeId?: number | null }> = ({
  thumbnailUrl,
  themeId,
}) => {
  const { elementRef, hasBeenVisible } = useIsVisible<HTMLDivElement>();

  const {
    thumbnailUrl: resolvedUrl,
    isLoading,
    isGenerating,
    isError,
  } = useSlideThumbnailPolling(thumbnailUrl, hasBeenVisible, themeId);

  if (isError) return null;

  return (
    <StyledPolledThumbnailWrapper ref={elementRef}>
      {!hasBeenVisible || isLoading || isGenerating ? (
        <SkeletonLoader count={1} height={140} width={200} single />
      ) : resolvedUrl ? (
        <StyledThumbnailImage src={resolvedUrl} alt="Slide thumbnail" />
      ) : null}
    </StyledPolledThumbnailWrapper>
  );
};

export const SlideRow = forwardRef<HTMLDivElement, SlideRowProps>(
  ({ slideNumber, title, thumbnailUrl, thumbnailBase64, viewStateKey, rightSlot, level, isHighlighted, onClick }, ref) => {
    const selectedThemeId = useSelector(viewStateSelectors.getSelectedThemeId(viewStateKey || ''));

    return (
    <StyledRow ref={ref} $level={level} $isHighlighted={isHighlighted} $clickable={!!onClick} onClick={onClick}>
      <StyledThumbnailContainer>
        {thumbnailUrl ? (
          <PolledThumbnailImage thumbnailUrl={thumbnailUrl} themeId={selectedThemeId} />
        ) : thumbnailBase64 ? (
          <StyledThumbnailImage src={`data:image/png;base64,${thumbnailBase64}`} alt="Slide thumbnail" />
        ) : (
          <SkeletonLoader count={1} height={140} width={200} single />
        )}
      </StyledThumbnailContainer>

      <StyledFooter>
        <StyledCaption>
          {slideNumber != null && <StyledSlideNumber>{String(slideNumber).padStart(2, '0')}</StyledSlideNumber>}

          {title && (
            <NgTooltip title={title} placement="top">
              <StyledSlideTitle>{title}</StyledSlideTitle>
            </NgTooltip>
          )}
        </StyledCaption>

        {rightSlot && <StyledRightSlot>{rightSlot}</StyledRightSlot>}
      </StyledFooter>
    </StyledRow>
    );
  },
);

SlideRow.displayName = 'SlideRow';
